import { Paper, Box, Typography, useTheme, alpha, Skeleton } from '@mui/material'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { useTranslation } from '@i18n'
import { useMemo } from 'react'

// ── Consistent status colors (matching StatsCards) ──
const STATUS_COLORS = {
    new: '#3b82f6',               // Blue
    read: '#ef4444',              // Red (Read/Pending)
    replied: '#10b981',           // Green
    pending_department: '#f59e0b', // Amber
    department_replied: '#06b6d4', // Cyan
    closed: '#475569',            // Slate
    archived: '#64748b',          // Gray
}

const StatusDistributionChart = ({ data = [], isLoading = false }) => {
    const { t, language } = useTranslation()
    const theme = useTheme()

    // Theme-based colors
    const bgPaper = theme.palette.background.paper
    const primaryColor = theme.palette.primary.main
    const dividerColor = theme.palette.divider

    const STATUS_LABELS = {
        new: t('stats.new', 'New'),
        read: t('stats.read', 'Read (Pending)'),
        replied: t('stats.replied', 'Replied'),
        pending_department: t('stats.pendingDepartment', 'Pending Dept.'),
        department_replied: t('stats.departmentReplied', 'Dept. Replied'),
        closed: t('stats.closed', 'Closed'),
        archived: t('stats.archived', 'Archived'),
    }

    // Transform data from API — show ALL statuses, even zero-count
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return []

        // Build a map from API data
        const dataMap = {}
        data.forEach(item => {
            dataMap[item.status] = item.count
        })

        // Build chart entries for every known status
        const allStatuses = ['new', 'read', 'replied', 'pending_department', 'department_replied', 'closed']
        return allStatuses
            .map(status => ({
                name: STATUS_LABELS[status] || status,
                value: dataMap[status] || 0,
                key: status
            }))
            .filter(item => item.value > 0)
    }, [data, language])

    const total = chartData.reduce((sum, item) => sum + item.value, 0)

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const item = payload[0]
            const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
            return (
                <Box
                    sx={{
                        bgcolor: alpha(bgPaper, 0.95),
                        backdropFilter: 'blur(20px)',
                        p: 2,
                        borderRadius: 2,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                        border: `1px solid ${alpha(dividerColor, 0.1)}`
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.payload.fill }} />
                        <Typography variant="body2" fontWeight={700}>
                            {item.name}
                        </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: item.payload.fill, mt: 0.5 }}>
                        {item.value} <Typography component="span" variant="body2" color="text.secondary">({percentage}%)</Typography>
                    </Typography>
                </Box>
            )
        }
        return null
    }

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        if (percent < 0.08) return null
        const RADIAN = Math.PI / 180
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5
        const x = cx + radius * Math.cos(-midAngle * RADIAN)
        const y = cy + radius * Math.sin(-midAngle * RADIAN)

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fontWeight={700}
                style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        )
    }

    const hasData = chartData.length > 0

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 4,
                height: '100%',
                background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(bgPaper, 0.9)} 0%, ${alpha(primaryColor, 0.05)} 100%)`
                    : `linear-gradient(135deg, ${bgPaper} 0%, ${alpha(primaryColor, 0.02)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(dividerColor, 0.08)}`,
                boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    boxShadow: `0 8px 40px ${alpha(primaryColor, 0.15)}`,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    {t('dashboard.statusDistribution', 'Status Distribution')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('dashboard.statusDescription', 'Breakdown by submission status')}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 240, position: 'relative' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Skeleton variant="circular" width={180} height={180} />
                    </Box>
                ) : hasData ? (
                    <>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="45%"
                                    innerRadius={50}
                                    outerRadius={85}
                                    paddingAngle={4}
                                    dataKey="value"
                                    labelLine={false}
                                    label={renderCustomizedLabel}
                                    animationBegin={0}
                                    animationDuration={1000}
                                    animationEasing="ease-out"
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={STATUS_COLORS[entry.key] || theme.palette.grey[500]}
                                            stroke={bgPaper}
                                            strokeWidth={3}
                                            style={{
                                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>

                        {/* Center total display */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '42%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center'
                            }}
                        >
                            <Typography
                                variant="h3"
                                fontWeight={800}
                                sx={{
                                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${theme.palette.secondary.main} 100%)`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}
                            >
                                {total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                {t('stats.total', 'Total')}
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('common.noData', 'No data available')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Legend — show all statuses with their colors */}
            {hasData && !isLoading && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 1, px: 1 }}>
                    {chartData.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: STATUS_COLORS[entry.key] || theme.palette.grey[500],
                                    boxShadow: `0 0 8px ${alpha(STATUS_COLORS[entry.key] || theme.palette.grey[500], 0.5)}`
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.68rem' }}>
                                {entry.name}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    )
}

export default StatusDistributionChart
